// ===========================================
// Pricing & Payment API Functions
// ===========================================
// API calls for pricing matrix, bank details, and payment options

import { apiClient } from '../services/api';

// ===========================================
// TYPES
// ===========================================

export type PricingCategory = 'INDIVIDUAL' | 'GROUP';

export interface ProgramLevelPricing {
  id: string;
  programLevelId: string;
  programLevel: {
    id: string;
    name: string;
    color: string | null;
    sortOrder: number;
  };
  durationId: string;
  duration: {
    id: string;
    minutes: number;
  };
  category: PricingCategory;
  price: number;
  isActive: boolean;
}

export interface PricingMatrix {
  programLevels: Array<{
    id: string;
    name: string;
    color: string | null;
    sortOrder: number;
    isActive: boolean;
  }>;
  durations: Array<{
    id: string;
    minutes: number;
    isActive: boolean;
  }>;
  prices: ProgramLevelPricing[];
}

export interface BankAccount {
  bsb: string;
  accountNumber: string;
  accountName: string;
}

export interface PaymentFrequencySettings {
  term: boolean;
  month: boolean;
  week: boolean;
  // Subscription options - parents can opt-in to auto-recurring payments
  termSubscriptionEnabled: boolean;
  monthSubscriptionEnabled: boolean;
  weekSubscriptionEnabled: boolean;
}

// Input types for mutations
export interface BulkPriceUpdate {
  programLevelId: string;
  durationId: string;
  category: PricingCategory;
  /** null CLEARS the cell (switches the price off) — the page's blank input. */
  price: number | null;
}

interface ApiResponse<T> {
  status: string;
  data: T;
}

// ===========================================
// FLEXIBLE PRICING TYPES
// ===========================================

export type PricingDimension = 'PROGRAM_LEVEL' | 'AGE_GROUP' | 'INSTRUMENT' | 'PRICE_ONLY';

export interface PricingConfig {
  dimension: PricingDimension;
}

export interface DimensionRow {
  id: string;
  name: string;
  color?: string | null;
  sortOrder: number;
  isActive: boolean;
}

export interface FlexiblePricingEntry {
  id: string;
  rowId: string;
  durationId: string;
  category: PricingCategory;
  price: number;
  isActive: boolean;
}

export interface FlexiblePricingMatrix {
  dimension: PricingDimension;
  rows: DimensionRow[];
  durations: Array<{ id: string; minutes: number; isActive: boolean }>;
  prices: FlexiblePricingEntry[];
}

export interface FlexibleBulkPriceUpdate {
  rowId: string;
  durationId: string;
  category: PricingCategory;
  price: number;
}

/** A class that bills a cell invoicing will ask for (backend `RequiredPriceClass`). */
export interface RequiredPriceClass {
  lessonId: string;
  lessonName: string;
  termName: string;
}

/** A matrix cell invoicing will ask for: blank or $0, and billed by a current/upcoming class. */
export interface RequiredPriceCell {
  programLevelId: string;
  programLevelName: string | null;
  durationMinutes: number;
  category: PricingCategory;
  zeroPrice: boolean;
  classes: RequiredPriceClass[];
}

/** GET /admin/pricing/matrix/required (backend `findRequiredPricing`). */
export interface RequiredPricing {
  terms: Array<{ id: string; name: string }>;
  cells: RequiredPriceCell[];
  noProgramLevel: RequiredPriceClass[];
}

// ===========================================
// PRICING MATRIX API
// ===========================================

export const pricingMatrixApi = {
  /**
   * Get the full pricing matrix (program levels x durations)
   */
  get: () =>
    apiClient.get<ApiResponse<PricingMatrix>>('/admin/pricing/matrix')
      .then((res) => res.data),

  /**
   * The cells invoicing will ask for (blank or $0, billed by a current or upcoming class)
   */
  getRequired: () =>
    apiClient.get<ApiResponse<RequiredPricing>>('/admin/pricing/matrix/required')
      .then((res) => res.data),

  /**
   * Bulk upsert prices in the pricing matrix
   */
  bulkUpdate: (prices: BulkPriceUpdate[]) =>
    apiClient.post<ApiResponse<PricingMatrix>>('/admin/pricing/matrix', { prices })
      .then((res) => res.data),

  /**
   * Update a single price entry
   */
  update: (id: string, data: { price?: number; isActive?: boolean }) =>
    apiClient.patch<ApiResponse<ProgramLevelPricing>>(`/admin/pricing/matrix/${id}`, data)
      .then((res) => res.data),

  /**
   * Delete a price entry (soft delete)
   */
  delete: (id: string) =>
    apiClient.delete<ApiResponse<void>>(`/admin/pricing/matrix/${id}`),
};

// ===========================================
// BANK ACCOUNT API
// ===========================================

export const bankAccountApi = {
  /**
   * Get bank account details
   */
  get: () =>
    apiClient.get<ApiResponse<BankAccount | null>>('/admin/pricing/bank-account')
      .then((res) => res.data),

  /**
   * Update bank account details
   */
  update: (data: BankAccount) =>
    apiClient.put<ApiResponse<BankAccount>>('/admin/pricing/bank-account', data)
      .then((res) => res.data),
};

// ===========================================
// PAYMENT FREQUENCIES API
// ===========================================

export const paymentFrequenciesApi = {
  /**
   * Get enabled payment frequencies
   */
  get: () =>
    apiClient.get<ApiResponse<PaymentFrequencySettings>>('/admin/pricing/payment-frequencies')
      .then((res) => res.data),

  /**
   * Update payment frequency settings
   */
  update: (data: PaymentFrequencySettings) =>
    apiClient.put<ApiResponse<PaymentFrequencySettings>>('/admin/pricing/payment-frequencies', data)
      .then((res) => res.data),
};

// ===========================================
// PRICING CONFIG API
// ===========================================

export const pricingConfigApi = {
  /**
   * Get the school's pricing dimension configuration
   */
  get: () =>
    apiClient.get<ApiResponse<PricingConfig>>('/admin/pricing/config')
      .then((res) => res.data),

  /**
   * Update the school's pricing dimension
   */
  update: (data: PricingConfig) =>
    apiClient.put<ApiResponse<PricingConfig>>('/admin/pricing/config', data)
      .then((res) => res.data),
};

// ===========================================
// FLEXIBLE PRICING MATRIX API
// ===========================================

export const flexiblePricingMatrixApi = {
  /**
   * Get the flexible pricing matrix (dimension rows x durations)
   */
  get: () =>
    apiClient.get<ApiResponse<FlexiblePricingMatrix>>('/admin/pricing/flexible-matrix')
      .then((res) => res.data),

  /**
   * Bulk upsert prices in the flexible pricing matrix
   */
  bulkUpdate: (prices: FlexibleBulkPriceUpdate[]) =>
    apiClient.post<ApiResponse<FlexiblePricingMatrix>>('/admin/pricing/flexible-matrix', { prices })
      .then((res) => res.data),
};
